import _ from "lodash";
import React, { useState, useCallback } from "react";
import { withRouter } from "react-router";
import PropTypes from "prop-types";
import { STAFF, STUDENT, PARENT } from "Constants/stringConstants";
import { connect } from "react-redux";
import { graphql, compose } from "lib/apolloClient";
import I18nHOC from "UIComponents/I18nHOC";
import { useDispatch } from "Hooks/useDispatch";

import {
  setMessagingUser,
  setGlobalTrayRetracted,
} from "Login/modules/LoginModule";
import { useI18n } from "Hooks";
import {
  getDefaultNameSortPreference,
  getUserId,
  getDefaultStudentNameOrderPreference,
  getSelectedAcademicYearId,
  getOrganizationId,
  getUserType,
} from "modules/CommonSelectors";
import {
  handleCreateNewChat,
  setMessagingSections,
  goToUserProfile,
} from "MessagingV2/modules/MessagingModule";
import { MESSAGING_SECTIONS } from "MessagingV2/modules/Utils";
import { getUserAccessPointDetailsQuery } from "MessagingV2/modules/MessagingQuery";
import { getIsStudentAlertsEnabled } from "StudentAlertsSettings/modules/StudentAlertsSettingsUtils";
import { getStudentAlertsQuery } from "AppComponents/StudentAlerts/modules/Queries";

import { BasicAccessPoint } from "./BasicAccessPoint";

import {
  isAccessPointEnable,
  getSubTitle1Component,
  getUserActions,
  getUserFooter,
  getSubtitle1WithData,
} from "./CommonComponents";

export const UserAccessPoint = props => {
  const {
    userType,
    avatarStyles,
    id,
    name,
    subtitle1,
    courses,
    studentFamilyMembers,
    familyStudents,
    isArchived,
    children,
    onProfileClick,
    profileImage,
    defaultNameSortPreference,
    defaultStudentNameOrderPreference,
    currentUserId,
    isPopoverOpen,
    openPopover,
    closePopover,
    isLoading,
    onMessagingRoute,
    curriculumProgramId,
    isCurrentCurriculumProgramUser,
    module,
    hideChatWithUser,
    popupWrapperCustomClasses,
    alerts,
    showAccessPoint,
    disabled = false,
  } = props;
  const { t } = useI18n(["common"]);
  const dispatch = useDispatch();

  const isStudentAlertsEnabled = getIsStudentAlertsEnabled();

  const handleOnMessagingClick = ({ id, isFamilyChannel = false }) => {
    if (onMessagingRoute) {
      dispatch(
        setMessagingSections({
          section: isFamilyChannel
            ? MESSAGING_SECTIONS.CHANNELS
            : MESSAGING_SECTIONS.CHAT,
        })
      );
      dispatch(handleCreateNewChat({ id, isFamilyChannel }));
    } else {
      dispatch(setMessagingUser({ id, isFamilyChannel }));
    }
    dispatch(setGlobalTrayRetracted(false));
  };

  const footerComponent = getUserFooter({
    userType,
    studentFamilyMembers,
    defaultNameSortPreference,
    defaultStudentNameOrderPreference,
    isLoading,
    familyStudents,
    courses,
    t,
    id,
    alerts,
    isStudentAlertsEnabled,
  });

  const actionComponent = getUserActions({
    t,
    userType,
    studentFamilyMembers,
    onMessagingClick: handleOnMessagingClick,
    defaultNameSortPreference,
    defaultStudentNameOrderPreference,
    id,
    name,
    isArchived,
    profileImage,
    currentUserId,
    closePopover,
    familyStudents,
    isLoading,
    hideChatWithUser,
  });

  const handleOnProfileClick = () => {
    if (_.isFunction(onProfileClick)) {
      onProfileClick(id);
      return;
    }
    dispatch(goToUserProfile({ id, userType, curriculumProgramId }));
  };

  const subtitle1Component = getSubTitle1Component({
    subtitle: subtitle1,
    isLoading,
  });

  const enableAccessPoint = isAccessPointEnable(userType, module);

  const isProfileClickActionLoading =
    _.isEqual(userType, STUDENT) &&
    !_.isFunction(onProfileClick) &&
    !curriculumProgramId &&
    !isCurrentCurriculumProgramUser &&
    isLoading;

  return showAccessPoint || !disabled ? (
    <BasicAccessPoint
      footerComponent={footerComponent}
      actionComponent={enableAccessPoint ? actionComponent : null}
      avatarStyles={avatarStyles}
      subtitle1={subtitle1Component}
      id={id}
      name={name}
      profileImage={profileImage}
      isArchived={isArchived}
      onProfileClick={handleOnProfileClick}
      isProfileClickActionLoading={isProfileClickActionLoading}
      openPopover={openPopover}
      closePopover={closePopover}
      isPopoverOpen={isPopoverOpen}
      popupWrapperCustomClasses={popupWrapperCustomClasses}
    >
      {children}
    </BasicAccessPoint>
  ) : (
    children
  );
};

const mapStateToProps = (state, ownProps) => {
  const defaultNameSortPreference = getDefaultNameSortPreference(state);
  const defaultStudentNameOrderPreference = getDefaultStudentNameOrderPreference(
    state
  );

  const onMessagingRoute = _.includes(
    _.get(ownProps, "location.pathname"),
    "/messaging"
  );

  const {
    userType,
    onProfileClick,
    curriculumProgramId,
    isCurrentCurriculumProgramUser,
  } = ownProps;

  const selectedAcademicYearId = getSelectedAcademicYearId(state);

  const skipCurriculumProgramFetch =
    !_.isEqual(userType, STUDENT) ||
    _.isFunction(onProfileClick) ||
    !!curriculumProgramId ||
    !!isCurrentCurriculumProgramUser;

  const currentUserType = getUserType(state);
  const showAccessPoint = currentUserType === STAFF;

  return {
    currentUserId: getUserId(state),
    defaultNameSortPreference,
    defaultStudentNameOrderPreference,
    onMessagingRoute,
    selectedAcademicYearId,
    skipCurriculumProgramFetch,
    isData: true,
    isLoading: false,
    showAccessPoint,
  };
};

const skip = props => {
  const {
    userType,
    isPopoverOpen,
    id,
    courses,
    subtitle1,
    studentFamilyMembers,
    skipCurriculumProgramFetch,
    showAccessPoint,
    disabled,
  } = props;

  if (!userType || !isPopoverOpen || !id || !showAccessPoint || disabled)
    return true;

  switch (userType) {
    case STAFF:
      return !_.isUndefined(courses) && !_.isUndefined(subtitle1);
    case STUDENT:
      return (
        !_.isUndefined(subtitle1) &&
        !_.isUndefined(studentFamilyMembers) &&
        skipCurriculumProgramFetch
      );
  }
  return false;
};

const UserAccessPointWithData = compose(
  withRouter,
  connect(mapStateToProps),
  I18nHOC({ resource: ["common"] }),
  graphql(getUserAccessPointDetailsQuery, {
    name: "getUserAccessPointDetails",
    skip,
    options: ({
      id,
      userType,
      subtitle1,
      courses,
      studentFamilyMembers,
      skipCurriculumProgramFetch,
      selectedAcademicYearId,
    }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id,
        userType: _.isEqual(userType, PARENT)
          ? "FAMILY_MEMBER"
          : _.toUpper(userType),
        skipGradeFetch:
          _.isEqual(userType, STUDENT) && !_.isUndefined(subtitle1),
        skipClassesFetch: _.isEqual(userType, STAFF) && !_.isUndefined(courses),
        skipFamilyMembersFetch:
          _.isEqual(userType, STUDENT) && !_.isUndefined(studentFamilyMembers),
        skipDisplayRoleFetch:
          _.isEqual(userType, STAFF) && !_.isUndefined(subtitle1),
        skipCurriculumProgramFetch,
        academicYearId: selectedAcademicYearId,
        organizationId: getOrganizationId(),
        portalType: "PLANNER",
      },
    }),
    props({
      getUserAccessPointDetails,
      ownProps: { isData, isLoading, ...rest },
    }) {
      const {
        subtitle1,
        courses,
        studentFamilyMembers,
        userType,
        curriculumProgramId,
        t,
      } = rest;
      const { node = {}, networkStatus } = getUserAccessPointDetails;
      const allGrades = _.get(node, "allGrades");
      const familyMembers = _.get(node, "familyMembers");
      const fetchedCourses = _.get(node, "courses");
      const displayRole = _.get(node, "displayRole");
      const curriculumPrograms = _.get(node, "curriculumPrograms", []);
      const curriculumProgramIdFromData = _.get(
        _.first(curriculumPrograms),
        "id"
      );

      const familyStudents = _.get(node, "children");

      const subtitle1WithData = !_.isUndefined(subtitle1)
        ? subtitle1
        : getSubtitle1WithData({
            userType,
            allGrades,
            displayRole,
            t,
          });

      return {
        subtitle1: !_.isUndefined(subtitle1) ? subtitle1 : subtitle1WithData,
        courses: !_.isUndefined(courses) ? courses : fetchedCourses,
        studentFamilyMembers: !_.isUndefined(studentFamilyMembers)
          ? studentFamilyMembers
          : familyMembers,
        curriculumProgramId: curriculumProgramId || curriculumProgramIdFromData,
        familyStudents,
        isData: !_.isEmpty(node) && isData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
      };
    },
  }),
  graphql(getStudentAlertsQuery, {
    name: "getStudentAlerts",
    skip: props => {
      const { id, userType } = props;
      return (
        !id ||
        userType !== STUDENT ||
        !getIsStudentAlertsEnabled() ||
        skip(props)
      );
    },
    options: ({ id }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id,
        filters: {
          status: "ACTIVE",
        },
      },
    }),
    props({
      getStudentAlerts: { networkStatus, node },
      ownProps: { isData, isLoading },
    }) {
      const edges = _.get(node, "alerts.edges", []);

      return {
        alerts: _.map(edges, "node"),
        isData: !_.isEmpty(node) && isData,
        isLoading: isLoading || _.includes([1, 2], networkStatus),
      };
    },
  })
)(UserAccessPoint);

const UserAccessPointWithPopoverState = props => {
  const { isOpen = false } = props;
  const [isPopoverOpen, setPopoverOpen] = useState(false);
  const openPopover = useCallback(() => setPopoverOpen(true), []);

  const debounceOpenPopover = useCallback(_.debounce(openPopover, 300), [
    openPopover,
  ]);

  const closePopover = useCallback(() => {
    // clear debounce
    debounceOpenPopover?.cancel();
    setPopoverOpen(false);
  }, [debounceOpenPopover]);

  return (
    <UserAccessPointWithData
      {...props}
      isPopoverOpen={isOpen || isPopoverOpen}
      openPopover={debounceOpenPopover}
      closePopover={closePopover}
    />
  );
};

UserAccessPointWithPopoverState.propTypes = {
  userType: PropTypes.oneOf([STAFF, STUDENT]).isRequired,
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  subtitle1: PropTypes.node,
  courses: PropTypes.arrayOf(PropTypes.object),
  onProfileClick: PropTypes.func,
  curriculumProgramId: PropTypes.string, // pass this for student if we know student curriculum program id
  isCurrentCurriculumProgramUser: PropTypes.bool, // if access point is added in screen where user must be part of current curriculum program
  profileImage: PropTypes.string.isRequired,
  isArchived: PropTypes.bool,
  avatarStyles: PropTypes.object,
  studentFamilyMembers: PropTypes.arrayOf(PropTypes.object),
  module: PropTypes.string,
  isOpen: PropTypes.bool, // Prop to control Popover's visibility from outside
};

export default UserAccessPointWithPopoverState;
